import { io } from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this._connectCallbacks = [];
    /** @type {Map<string, Set<Function>>} */
    this._listenerRegistry = new Map();
  }

  _track(event, callback) {
    if (!this._listenerRegistry.has(event)) {
      this._listenerRegistry.set(event, new Set());
    }
    this._listenerRegistry.get(event).add(callback);
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  _untrack(event, callback) {
    const set = this._listenerRegistry.get(event);
    if (set) {
      set.delete(callback);
      if (set.size === 0) {
        this._listenerRegistry.delete(event);
      }
    }
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  _rebindAllListeners() {
    if (!this.socket) return;
    for (const [event, callbacks] of this._listenerRegistry) {
      for (const cb of callbacks) {
        this.socket.off(event, cb);
        this.socket.on(event, cb);
      }
    }
  }

  connect(token) {
    if (this.socket) {
      if (this.connected) return this.socket;
      this.socket.disconnect();
      this.socket = null;
    }

    const serverUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:4000';
    const socketUrl = serverUrl.replace('/api', '');

    console.log('Connecting to Socket.IO server at:', socketUrl);

    this.socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      timeout: 5000,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    this.socket.on('connect', () => {
      this.connected = true;
      console.log('Connected to server via WebSocket');
      this.subscribeToTargets();
      this._rebindAllListeners();
      this._connectCallbacks.forEach((cb) => {
        try {
          cb();
        } catch (e) {
          /* ignore */
        }
      });
    });

    this.socket.on('disconnect', () => {
      this.connected = false;
      console.log('Disconnected from server');
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
      this.connected = false;
    });

    this._rebindAllListeners();

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.connected = false;
    this._connectCallbacks = [];
    this._listenerRegistry.clear();
  }

  onConnect(callback) {
    this._connectCallbacks.push(callback);
    return () => {
      this._connectCallbacks = this._connectCallbacks.filter((cb) => cb !== callback);
    };
  }

  isConnected() {
    return this.connected && this.socket?.connected;
  }

  getSocket() {
    return this.socket;
  }

  subscribeToTargets() {
    if (this.socket) {
      this.socket.emit('subscribe-targets');
    }
  }

  subscribeToTarget(targetId) {
    if (this.socket && targetId != null && Number.isFinite(Number(targetId))) {
      this.socket.emit('subscribe-target', Number(targetId));
    }
  }

  unsubscribeFromTarget(targetId) {
    if (this.socket && targetId != null && Number.isFinite(Number(targetId))) {
      this.socket.emit('unsubscribe-target', Number(targetId));
    }
  }

  on(event, callback) {
    this._track(event, callback);
  }

  off(event, callback) {
    this._untrack(event, callback);
  }

  onStatusUpdate(callback) {
    this._track('status-update', callback);
  }

  offStatusUpdate(callback) {
    this._untrack('status-update', callback);
  }

  onStatusUpdateDetail(callback) {
    this._track('status-update-detail', callback);
  }

  onTargetStatusAlert(callback) {
    this._track('target-status-alert', callback);
  }

  offTargetStatusAlert(callback) {
    this._untrack('target-status-alert', callback);
  }

  offStatusUpdateDetail(callback) {
    this._untrack('status-update-detail', callback);
  }

  onTargetCreated(callback) {
    this._track('target-created', callback);
  }

  offTargetCreated(callback) {
    this._untrack('target-created', callback);
  }

  onTargetUpdated(callback) {
    this._track('target-updated', callback);
  }

  offTargetUpdated(callback) {
    this._untrack('target-updated', callback);
  }

  onTargetDeleted(callback) {
    this._track('target-deleted', callback);
  }

  offTargetDeleted(callback) {
    this._untrack('target-deleted', callback);
  }

  onNotification(callback) {
    this._track('notification', callback);
  }

  onAgentProcessUpdate(callback) {
    this._track('agent-process-update', callback);
  }

  onAgentServiceUpdate(callback) {
    this._track('agent-service-update', callback);
  }

  onAgentResourcesUpdate(callback) {
    this._track('agent-resources-update', callback);
  }

  onAgentError(callback) {
    this._track('agent-error', callback);
  }

  onAgentRegistered(callback) {
    this._track('agent:registered', callback);
  }
}

const socketService = new SocketService();
export default socketService;
